import type { MessageKey } from '@bookingdei/i18n/message-key';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { useLanguage } from '../../shell/language-context';
import { NavIcon } from '../../shell/nav-icon';
import type { NavIconName } from '../../shell/nav-icon';
import { PERIODS } from './home-model';
import type { KpiFigures, Period, Trend } from './home-model';
import { useHomeMoney } from './use-home-money';

const PERIOD_KEY: Record<Period, MessageKey> = {
  '1d': 'dc71.period.1d',
  '7d': 'dc71.period.7d',
  '30d': 'dc71.period.30d',
  '12m': 'dc71.period.12m',
};
const VS_KEY: Record<Period, MessageKey> = {
  '1d': 'dc71.vs.1d',
  '7d': 'dc71.vs.7d',
  '30d': 'dc71.vs.30d',
  '12m': 'dc71.vs.12m',
};

// "At a glance" (M:417-469, kpiCards M:8302): a period segment (Yesterday · 7 days ·
// 30 days · 12 months → insights `range` 1d/7d/30d/12m, API-123) and four cards —
// Bookings, Completed, Revenue, Rating — each with a trend chip against the previous
// window of equal length (`prev`). Without `prev` (bookings-list fallback) the chip
// row carries the comparison label alone.
export function HomeKpis({
  period,
  onPeriod,
  figures,
}: {
  period: Period;
  onPeriod: (p: Period) => void;
  figures: KpiFigures | null;
}) {
  const { t, lang } = useLanguage();
  const money = useHomeMoney();
  const n = (v: number) => formatNumber(lang, v);
  const f = figures;

  const trendText = (tr: Trend) =>
    tr.kind === 'pct' ? formatMessage(lang, 'dc71.kpi.pct', { n: tr.value }) : formatNumber(lang, tr.value, { minFractionDigits: 1, maxFractionDigits: 1 });

  const cards: { icon: NavIconName; label: string; value: string; sub: string; trend: Trend | null }[] = [
    {
      icon: 'calendar',
      label: t('dc71.kpi.bookings'),
      value: f ? n(f.bookings) : '—',
      sub: f ? formatMessage(lang, 'dc71.kpi.cancelled', { n: f.cancelled }) : '',
      trend: f?.trends.bookings ?? null,
    },
    {
      icon: 'chart',
      label: t('dc71.kpi.completed'),
      value: f ? n(f.completed) : '—',
      sub: f ? formatMessage(lang, 'dc71.kpi.completion', { pct: f.completionPct }) : '',
      trend: f?.trends.completed ?? null,
    },
    {
      icon: 'taka',
      label: t('dc71.kpi.revenue'),
      value: f ? money(f.revenueMinor, true) : '—',
      sub: f ? formatMessage(lang, 'dc71.kpi.avg', { amount: money(f.avgMinor) }) : '',
      trend: f?.trends.revenue ?? null,
    },
    {
      icon: 'star',
      label: t('dc71.kpi.rating'),
      value: f && f.rating !== null ? formatNumber(lang, f.rating, { minFractionDigits: 1, maxFractionDigits: 1 }) : '—',
      sub: f ? (f.reviews > 0 ? formatMessage(lang, 'dc71.kpi.reviews', { n: f.reviews }) : t('dc71.kpi.no_reviews')) : '',
      trend: f?.trends.rating ?? null,
    },
  ];

  return (
    <section className="hm-glance" aria-labelledby="hm-glance-title">
      <div className="hm-glance__head">
        <h3 id="hm-glance-title" className="hm-card__title">
          {t('dc71.overview')}
        </h3>
        <div className="hm-seg" role="group" aria-label={t('dc71.period.label')}>
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              data-period={p}
              aria-pressed={p === period}
              className={p === period ? 'hm-seg__btn is-on' : 'hm-seg__btn'}
              onClick={() => onPeriod(p)}
            >
              {t(PERIOD_KEY[p])}
            </button>
          ))}
        </div>
      </div>
      <div className="hm-kpis" key={period}>
        {cards.map((c, i) => (
          <div key={c.icon} className={`hm-kpi hm-kpi--${i + 1}`}>
            <div className="hm-kpi__top">
              <div className="hm-kpi__head">
                <span className="hm-kpi__chip">
                  <NavIcon name={c.icon} size={19} strokeWidth={1.75} />
                </span>
                <span className="hm-kpi__label">{c.label}</span>
              </div>
              <div className="hm-kpi__fig">
                <div className="hm-kpi__value">{c.value}</div>
                <span className="hm-kpi__sub">{c.sub}</span>
              </div>
            </div>
            <div className="hm-kpi__foot">
              {c.trend && (
                <span className={c.trend.up ? 'hm-trend hm-trend--up' : 'hm-trend hm-trend--down'}>
                  {c.trend.up ? '↗' : '↘'} {trendText(c.trend)}
                </span>
              )}
              <span className="hm-kpi__vs">{t(VS_KEY[period])}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
